import type {
	CellClassParams,
	ColDef,
	ColumnWidthCallbackParams,
	GridApi,
	ValueGetterParams,
} from "ag-grid-community";
import { round } from "lodash-es";
import { MILL_1, MILL_2, type MillId } from "@/setup/mill-config/mill-config";
import { SECTION_ASSET_LABEL } from "../../models/SectionTableRow";
import type {
	SectionExport,
	SectionsExportData,
} from "../../models/SectionExport";
import {
	EXCEL_STYLE,
	type ExcelNumberStyle,
	isBanded,
} from "./sectionExportStyles";

export interface SectionsSheetRow {
	Section: SectionExport;
	GroupIndex: number;
}

export const EXPORT_MILLS = [
	{ label: SECTION_ASSET_LABEL.MILL_1, millId: MILL_1.id },
	{ label: SECTION_ASSET_LABEL.MILL_2, millId: MILL_2.id },
];

export const buildSectionsSheetRows = (
	data: SectionsExportData,
	millId: MillId,
): SectionsSheetRow[] =>
	data.sections
		.filter((section) => section.AssetId === millId)
		.toSorted(
			(a, b) =>
				Number(b.HasProductionEvents) - Number(a.HasProductionEvents) ||
				a.SectionName.localeCompare(b.SectionName) ||
				a.SectionId - b.SectionId,
		)
		.map((section, index) => ({ Section: section, GroupIndex: index }));

const resolveEffectiveTPH = (section: SectionExport) =>
	section.TargetTPH == null
		? null
		: round((section.TargetTPH * (section.TargetTPHPercentage ?? 80)) / 100, 2);

const sectionValue =
	(getValue: (row: SectionsSheetRow) => string | number | null) =>
	(params: ValueGetterParams<SectionsSheetRow>) =>
		params.data ? getValue(params.data) : null;

const isEmpty = (params: CellClassParams<SectionsSheetRow>) =>
	params.value == null || params.value === "";

const SECTION_CLASS_RULES = {
	[EXCEL_STYLE.BAND]: (params: CellClassParams<SectionsSheetRow>) =>
		isBanded(params) && !isEmpty(params),
	[EXCEL_STYLE.EMPTY]: (params: CellClassParams<SectionsSheetRow>) =>
		isEmpty(params) && params.data?.Section.HasProductionEvents === true,
	[EXCEL_STYLE.EMPTY_UNUSED]: (params: CellClassParams<SectionsSheetRow>) =>
		isEmpty(params) && params.data?.Section.HasProductionEvents !== true,
};

const textColumn = (
	headerName: string,
	getValue: (row: SectionsSheetRow) => string | number | null,
	extraClasses: string[] = [],
): ColDef<SectionsSheetRow> => ({
	headerName,
	valueGetter: sectionValue(getValue),
	cellClass: [EXCEL_STYLE.TEXT, ...extraClasses],
	cellClassRules: SECTION_CLASS_RULES,
});

const numberColumn = (
	headerName: string,
	style: ExcelNumberStyle,
	getValue: (section: SectionExport) => number | null,
): ColDef<SectionsSheetRow> => ({
	headerName,
	valueGetter: sectionValue((row) => getValue(row.Section)),
	cellClass: style,
	cellClassRules: SECTION_CLASS_RULES,
});

const SECTIONS_SHEET_COLUMNS: ColDef<SectionsSheetRow>[] = [
	{
		...textColumn("Section", (row) => row.Section.SectionName, [
			EXCEL_STYLE.SECTION,
		]),
		pinned: "left",
	},
	textColumn("Section Family", (row) => row.Section.SectionFamilyName),
	textColumn("Description", (row) => row.Section.Description),
	numberColumn(
		"Budget TPH",
		EXCEL_STYLE.DECIMAL,
		(section) => section.TonsPerHour,
	),
	numberColumn(
		"Target TPH",
		EXCEL_STYLE.DECIMAL,
		(section) => section.TargetTPH,
	),
	numberColumn(
		"TPH Pct.",
		EXCEL_STYLE.PERCENT,
		(section) => section.TargetTPHPercentage,
	),
	numberColumn("Effective TPH", EXCEL_STYLE.DECIMAL, resolveEffectiveTPH),
	numberColumn(
		"LBs / Foot",
		EXCEL_STYLE.DECIMAL,
		(section) => section.LBsPerFoot,
	),
	numberColumn(
		"Billet Size",
		EXCEL_STYLE.INTEGER,
		(section) => section.BilletSize,
	),
	numberColumn(
		"Mill Yield",
		EXCEL_STYLE.PERCENT,
		(section) => section.MillYield,
	),
	numberColumn(
		"Finished Yield",
		EXCEL_STYLE.PERCENT,
		(section) => section.FinishedYield,
	),
	textColumn(
		"Flow Sheets",
		({ Section: section }) =>
			section.StandDrawingCount == null || section.StandCount == null
				? null
				: `${section.StandDrawingCount}/${section.StandCount}`,
		[EXCEL_STYLE.CENTER],
	),
	textColumn(
		"Production Events",
		(row) => (row.Section.HasProductionEvents ? "Yes" : "No"),
		[EXCEL_STYLE.CENTER],
	),
];

const MILL_2_ONLY_COLUMNS = new Set(["Section Family", "Flow Sheets"]);

export const buildSectionsSheetColumns = (millId: MillId) =>
	millId === MILL_2.id
		? SECTIONS_SHEET_COLUMNS
		: SECTIONS_SHEET_COLUMNS.filter(
				(column) => !MILL_2_ONLY_COLUMNS.has(column.headerName ?? ""),
			);

const CHAR_WIDTH = 7;
const CELL_PADDING = 16;
const MIN_COLUMN_WIDTH = 60;
const MAX_COLUMN_WIDTH = 400;

const displayLength = (value: unknown) =>
	typeof value === "number"
		? value.toLocaleString("en-US", {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			}).length
		: String(value ?? "").length;

export const fitSectionsSheetColumnWidth =
	(api: GridApi<SectionsSheetRow>) =>
	({ column }: ColumnWidthCallbackParams) => {
		if (!column) return MIN_COLUMN_WIDTH;

		const headerWords = (column.getColDef().headerName ?? "").split(" ");
		let chars = Math.max(...headerWords.map((word) => word.length));
		api.forEachNode((rowNode) => {
			chars = Math.max(
				chars,
				displayLength(api.getCellValue({ rowNode, colKey: column })),
			);
		});

		return Math.min(
			MAX_COLUMN_WIDTH,
			Math.max(MIN_COLUMN_WIDTH, chars * CHAR_WIDTH + CELL_PADDING),
		);
	};
